import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, type ChatSession, type ChatMessage } from '@/api/client';

interface UseChatHistoryOptions {
  contextType?: string;
  contextId?: string;
  /** Set to false to skip loading sessions (default: true) */
  enabled?: boolean;
}

interface UseChatHistoryReturn {
  sessions: ChatSession[];
  currentSessionId: string | null;
  loading: boolean;
  error: string | null;
  createNewSession: (title?: string) => Promise<ChatSession>;
  loadSession: (sessionId: string) => Promise<ChatMessage[]>;
  deleteSession: (sessionId: string) => Promise<void>;
  restoreSession: (sessionId: string) => Promise<void>;
  updateSessionTitle: (sessionId: string, title: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
  setCurrentSessionId: (sessionId: string | null) => void;
}

export function useChatHistory(options?: UseChatHistoryOptions): UseChatHistoryReturn {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [activeSessions, deletedSessions] = await Promise.all([
        apiClient.listChatSessions(false, options?.contextType, options?.contextId),
        apiClient.listChatSessions(true, options?.contextType, options?.contextId)
      ]);

      // Combine active and deleted sessions, with deleted ones marked
      const allSessions = [
        ...activeSessions.sessions,
        ...deletedSessions.sessions.filter(s => s.deleted_at)
      ];

      setSessions(allSessions);
    } catch (err: any) {
      console.error('Failed to load chat sessions:', err);
      setError('Failed to load chat history');
    } finally {
      setLoading(false);
    }
  }, [options?.contextType, options?.contextId]);

  const createNewSession = useCallback(async (title?: string): Promise<ChatSession> => {
    try {
      const newSession = await apiClient.createChatSession({
        title,
        context_type: options?.contextType,
        context_id: options?.contextId,
      });
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.id);
      return newSession;
    } catch (err: any) {
      console.error('Failed to create new session:', err);
      throw new Error('Failed to create new chat session');
    }
  }, [options?.contextType, options?.contextId]);

  const loadSession = useCallback(async (sessionId: string): Promise<ChatMessage[]> => {
    try {
      // Load the session to make it active
      await apiClient.loadChatSession(sessionId);

      // Get the messages for display
      const response = await apiClient.getSessionMessages(sessionId);

      setCurrentSessionId(sessionId);
      return response.messages;
    } catch (err: any) {
      console.error('Failed to load session:', err);
      throw new Error('Failed to load chat session');
    }
  }, []);

  const deleteSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      await apiClient.deleteChatSession(sessionId);

      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId
          ? { ...s, deleted_at: new Date().toISOString() }
          : s
      ));

      // If this was the current session, clear it
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
      }
    } catch (err: any) {
      console.error('Failed to delete session:', err);
      throw new Error('Failed to delete chat session');
    }
  }, [currentSessionId]);

  const restoreSession = useCallback(async (sessionId: string): Promise<void> => {
    try {
      await apiClient.restoreChatSession(sessionId);

      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId
          ? { ...s, deleted_at: undefined }
          : s
      ));
    } catch (err: any) {
      console.error('Failed to restore session:', err);
      throw new Error('Failed to restore chat session');
    }
  }, []);

  const updateSessionTitle = useCallback(async (sessionId: string, title: string): Promise<void> => {
    try {
      await apiClient.updateChatSession(sessionId, { title });

      // Update local state
      setSessions(prev => prev.map(s =>
        s.id === sessionId
          ? { ...s, title, updated_at: new Date().toISOString() }
          : s
      ));
    } catch (err: any) {
      console.error('Failed to update session title:', err);
      throw new Error('Failed to update session title');
    }
  }, []);

  // Load sessions on mount
  const enabled = options?.enabled !== false;
  useEffect(() => {
    if (enabled) {
      refreshSessions();
    } else {
      setLoading(false);
    }
  }, [refreshSessions, enabled]);

  // Refresh sessions when current session changes (to pick up new sessions)
  // But only if the session is not already in our list, and only retry once per session ID
  const triedMissingSessionIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (currentSessionId && !sessions.find(s => s.id === currentSessionId)) {
      if (triedMissingSessionIds.current.has(currentSessionId)) return;
      triedMissingSessionIds.current.add(currentSessionId);
      const timer = setTimeout(() => {
        refreshSessions();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentSessionId, sessions, refreshSessions]);

  return {
    sessions,
    currentSessionId,
    loading,
    error,
    createNewSession,
    loadSession,
    deleteSession,
    restoreSession,
    updateSessionTitle,
    refreshSessions,
    setCurrentSessionId,
  };
}
